import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

import AddCallIcon from '@mui/icons-material/AddIcCall';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import { ClassNameMap, makeStyles } from '@mui/styles';

import {
  DayOfWeek,
  Enum,
  MemberContactTimeType,
  MemberEngagementLevel,
  ProgramTaskReviewStatus,
  ProgramTaskStatus,
} from '@vestahealthcare/common/enums';
import { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  CCMTask,
  PaginationType,
  PodConfig,
  PodRule,
} from '@vestahealthcare/common/models';
import {
  DATE_FORMAT_SHORT,
  EMPTY,
  TIME_FORMAT,
} from '@vestahealthcare/common/utils/constants';

import {
  Actions,
  BackgroundColors,
  Chip,
  CollapseIcon,
  Colors,
  Fonts,
  IconButton,
  MemberInfo,
  StatusIndicator,
  Table,
  TableItem,
  Tooltip,
  UpdatedBy,
} from 'styleguide-v2';

import MemberCCMTaskAction from 'dash/src/pages/MemberProfile/CCMTasks/types/MemberCCMTaskAction';
import Session from 'dash/src/services/SessionServices';

import { EngagementIcon } from '../../../components/Icons/EngagementIcon';

export interface TaskTableModel extends CCMTask, TableItem {
  actionLoading?: boolean;
}

interface Props {
  child?: boolean;
  data: {
    pods: PodConfig[];
    taskDefinitions: PodRule[];
  };
  tasks: CCMTask[];
  pagination?: PaginationType;
  onChangePage?: (page: number) => void;
  onChangePageSize?: (pageSize: number) => void;
  onClickRow?: (task: CCMTask) => void;
  onLogCall?: (task: CCMTask) => void;
  onTaskAction?: (action: Selectable, event: CCMTask, index: number) => void;
}

const useStyles = makeStyles({
  iconMargin: {
    marginLeft: '0.5rem',
  },
  minutes: {
    borderRadius: '0.25rem',
    display: 'inline-block',
    fontWeight: 500,
    minWidth: '3rem',
    padding: '0.5rem 0.75rem',
    textAlign: 'center',
  },
  warningMinutes: {
    background: BackgroundColors.lightYellow,
  },
  errorMinutes: {
    color: Colors.textRed2,
    background: `${Colors.textRed}33`,
  },
  table: {
    '&& tbody': {
      '& td': {
        padding: '6px',
        fontSize: `calc(${Fonts.fontSize} * 0.75)`,
      },
      '& a': {
        fontSize: `calc(${Fonts.fontSize} * 0.75)`,
      },
      '& > tr.detail > td': {
        background: Colors.white,
        padding: '0',
      },
    },
  },
  noPaddingTable: {
    paddingTop: 0,

    '&& thead': {
      display: 'none',
    },
    '&& tbody': {
      borderRadius: 0,
      boxShadow: 'none',

      '& tr td': {
        backgroundColor: `${BackgroundColors.lighterGray}!important`,
      },
    },
    '&& tbody tbody': {
      borderLeft: 0,
      borderRight: 0,
    },
  },
});

const CCM_TASK_COLUMNS = (
  { child, data, onClickRow, onLogCall, onTaskAction }: Props,
  showHouseHoldMembers: boolean,
  styles: ClassNameMap<
    'iconMargin' | 'warningMinutes' | 'errorMinutes' | 'minutes'
  >,
  onToggleDetail: (task: TaskTableModel, index: number) => void,
) => [
  {
    headerName: translate('ccmTasks.table.member'),
    component: ({ member }: CCMTask) =>
      child ? (
        <></>
      ) : (
        <>
          <MemberInfo
            icon={
              <>
                {member.engagementLevel && (
                  <Tooltip
                    text={translate('ccmTasks.table.engagementLevel', {
                      level: member.engagementLevel.toString(),
                    })}
                  >
                    <div className="flex">
                      <EngagementIcon
                        color={MemberEngagementLevel.getColor(
                          member.engagementLevel,
                        )}
                        size="small"
                      />
                    </div>
                  </Tooltip>
                )}
                {member.unreachable && (
                  <Tooltip text={translate('ccmTasks.table.unreachable')}>
                    <PhoneDisabledIcon color="error" />
                  </Tooltip>
                )}
                {member.liteToCcm && (
                  <Chip
                    color="secondary"
                    title={translate('ccmTasks.table.ccmActiveTag')}
                    type="outlined"
                    size="small"
                  >
                    <div className="flex min-gap semi-bold">
                      LITE
                      <ArrowForwardIcon
                        color="primary"
                        fontSize="small"
                        sx={{ marginTop: '-0.125rem' }}
                      />
                      CCM
                    </div>
                  </Chip>
                )}
                {member.guideEligible && (
                  <Chip
                    color="pink"
                    title={translate('ccmTasks.table.guideEligible')}
                    type="contained"
                    size="small"
                  >
                    <span className="semi-bold">G</span>
                  </Chip>
                )}
              </>
            }
            member={member}
            link={onClickRow ? `#/patients/${member?.id}` : undefined}
            showHouseHoldMembers={showHouseHoldMembers}
            showLanguage
            showState
          />
        </>
      ),
    width: 225,
  },
  {
    headerName: translate('ccmTasks.table.task'),
    component: ({ taskDate, name }: CCMTask) =>
      taskDate ? (
        <UpdatedBy user={name} date={taskDate} dateFormat={DATE_FORMAT_SHORT} />
      ) : (
        name
      ),
  },
  {
    headerName: translate('ccmTasks.table.taskStatus'),
    component: ({ adminStatus, shouldShowReviewStatus, status }: CCMTask) =>
      status ? (
        <StatusIndicator
          status={status.toString()}
          subtitle={
            shouldShowReviewStatus
              ? (adminStatus || ProgramTaskReviewStatus.NOT_REVIEWED).toString()
              : ''
          }
          color={ProgramTaskStatus.getColor(status)}
        />
      ) : (
        EMPTY
      ),
    width: 175,
  },
  {
    headerName: translate('ccmTasks.table.contactPreference'),
    component: ({ id, member }: CCMTask) =>
      member
        .getDaysOfService()
        ?.map(({ dates, startTime, endTime, type }, index) => (
          <p className="no-margin" key={`ccm-task-${id}-days-${index}`}>
            {!!dates?.length && (
              <span>
                {DayOfWeek.stringify(dates)}
                {(type || startTime || endTime) && ': '}
              </span>
            )}
            {type && type !== MemberContactTimeType.CUSTOM && (
              <span>{`${type?.toString()} `}</span>
            )}
            {(startTime || endTime) && (
              <span>
                {startTime
                  ? moment(startTime.getTime()).format(TIME_FORMAT)
                  : '*'}
                {' - '}
                {endTime ? moment(endTime.getTime()).format(TIME_FORMAT) : '*'}
              </span>
            )}
          </p>
        )) || EMPTY,

    width: 150,
  },
  {
    headerName: translate('ccmTasks.table.attempts'),
    component: ({ attempts }: CCMTask) =>
      attempts !== undefined ? attempts : EMPTY,
    width: 75,
  },
  {
    headerName: translate('ccmTasks.table.validMins'),
    component: ({ ccmValidMinutes }: CCMTask) =>
      ccmValidMinutes ? (
        <Tooltip text={ccmValidMinutes}>
          <CheckCircleIcon className={styles.iconMargin} color="primary" />
        </Tooltip>
      ) : (
        <CancelIcon className={styles.iconMargin} color="error" />
      ),
    width: 150,
  },
  {
    headerName: translate('ccmTasks.table.ccmMinMinutesNextCpt'),
    component: ({ minMinutesNextCPT }: CCMTask) => {
      if (minMinutesNextCPT === null)
        return (
          <span className={classNames(styles.errorMinutes, styles.minutes)}>
            MAX
          </span>
        );

      if (!minMinutesNextCPT) return EMPTY;
      return (
        <span className={classNames(styles.warningMinutes, styles.minutes)}>
          {minMinutesNextCPT}
        </span>
      );
    },
    width: 150,
  },
  {
    headerName: translate('ccmTasks.table.callback'),
    component: ({ callback }: CCMTask) =>
      callback ? <UpdatedBy date={callback} contrast /> : EMPTY,

    width: 150,
  },
  {
    headerName: ' ',
    component: (task: TaskTableModel, index: number) => {
      const actions = MemberCCMTaskAction.asActionsArray(
        task,
        data.taskDefinitions?.find(({ name }) => name === task.name),
        data.pods?.find(({ id }) => id === task.member?.pod?.id),
        Session.actingUser,
      ).filter((action) => action !== MemberCCMTaskAction.FORCE_OVERRIDE);

      return (
        <div className="flex gap spaced">
          {onLogCall ? (
            <IconButton
              size="small"
              onClick={(evt) => {
                evt.stopPropagation();
                onLogCall(task);
              }}
              tooltip={translate('ccmTasks.table.logCall')}
            >
              <AddCallIcon />
            </IconButton>
          ) : (
            <div style={{ width: '3.3rem' }} />
          )}
          {onTaskAction && actions?.length ? (
            <Actions
              actions={Enum.toSelectable(actions)}
              data-cy={`ccm-task-item-${index}-actions`}
              loading={task.actionLoading}
              onActionSelected={(action) => onTaskAction(action, task, index)}
            />
          ) : (
            <div style={{ width: '3.3rem' }} />
          )}
          {!child && task.children?.length ? (
            <IconButton
              size="small"
              onClick={(evt) => {
                evt.stopPropagation();
                onToggleDetail(task, index);
              }}
            >
              <CollapseIcon
                open={!!task.open}
                data-cy={`ccm-task-item-${index}-collapse`}
              />
            </IconButton>
          ) : (
            <div style={{ width: '4.1rem' }} />
          )}
        </div>
      );
    },
    width: 150,
  },
];

export const CCMTasksTable = (props: Props) => {
  const styles = useStyles();

  const [data, setData] = useState<TaskTableModel[]>([]);

  const onToggleDetail = (item: TaskTableModel) => {
    item.open = !item.open;
    if (item.open) {
      item.detail = (
        <CCMTasksTable
          child
          data={props.data}
          tasks={item.children || []}
          onClickRow={props.onClickRow}
        />
      );
    } else {
      delete item.detail;
    }
    setData([...data]);
  };

  const columns = useMemo(
    () => CCM_TASK_COLUMNS(props, true, styles, onToggleDetail),
    [props.onLogCall],
  );

  useEffect(() => setData(props.tasks), [props.data]);

  return (
    <Table
      className={classNames(styles.table, props.child && styles.noPaddingTable)}
      config={{
        columns,
        compact: true,
        data,
        detail: true,
        defaultPageSize: 25,
        getRowBrand: ({ member }: CCMTask) => {
          if (member.isVPC() || member.isVPCLite()) return 'VPC';
          return member.brand;
        },
        pagination: props.pagination,
        paginationOptions: props.child ? [-1] : undefined,
      }}
      empty
      fixedHeader
      onChangePage={props.onChangePage}
      onChangeRowsPerPage={props.onChangePageSize}
      onClickRow={props.onClickRow}
    />
  );
};

export default CCMTasksTable;
